/**
 * AdminPromptManagement Component (UI-1)
 * Main prompt management page for Q AI Admin Panel
 *
 * Features:
 * - Stats cards: Total, Active, System, Rules (clickable to filter)
 * - Search bar with category filter
 * - Data table with actions (View, Edit, Test, History, Delete)
 * - Lock icon for required prompts
 * - Pagination (5/10/25/50 per page)
 * - Refresh cache button
 * - Export Dialog: Downloads prompts_export_YYYY-MM-DD.json
 * - Import Dialog: Drag & drop file upload OR paste JSON with real-time validation
 * - View Prompt Dialog: Full prompt details with copy to clipboard
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    TextField,
    InputAdornment,
    Select,
    MenuItem,
    FormControl,
    Chip,
    Tooltip,
    Pagination,
    Button,
    Skeleton,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Tabs,
    Tab
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import ScienceIcon from '@mui/icons-material/Science';
import HistoryIcon from '@mui/icons-material/History';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import ChatIcon from '@mui/icons-material/Chat';
import CodeIcon from '@mui/icons-material/Code';
import { toast } from 'react-toastify';

import { getPrompts, deletePrompt, exportPrompts, bulkImportPrompts, PROMPT_CATEGORIES, MQL5_CATEGORIES, QAI_CHAT_CATEGORIES, METADATA_TIERS } from '../../api/promptsApi';
import EditPromptModal from './EditPromptModal';
import TestPromptModal from './TestPromptModal';
import VersionHistoryModal from './VersionHistoryModal';
import MQL5PromptsTab from './MQL5PromptsTab';

// Embedded theme
const theme = {
    colors: {
        bg: { primary: '#191b20', secondary: '#292b2f', tertiary: '#333539', quaternary: '#3f4147' },
        text: { primary: 'rgba(255,255,255,1)', secondary: 'rgba(255,255,255,0.9)', tertiary: 'rgba(255,255,255,0.85)', quaternary: 'rgba(255,255,255,0.75)', placeholder: 'rgba(255,255,255,0.6)' },
        accent: { primary: '#7c3aed', secondary: '#5b21b6', tertiary: '#a78bfa' },
        border: { primary: 'rgba(255,255,255,0.1)', secondary: 'rgba(255,255,255,0.05)', tertiary: 'rgba(255,255,255,0.15)' },
        status: { success: '#22c55e', error: '#ef4444', warning: '#f59e0b', info: '#3b82f6' }
    },
    borderRadius: { sm: '8px', md: '12px' },
    transitions: { hover: '200ms cubic-bezier(0.4,0,0.2,1)' }
};

// Category colors
const categoryColors = {
    system: '#7c3aed',
    context: '#3b82f6',
    rules: '#f59e0b',
    examples: '#22c55e',
    training: '#10b981',
    analysis: '#6366f1',
    // Q.AI Chat categories
    qai_chat: '#ec4899',
    mt5_chart: '#f97316',
    // MQL5 categories
    mql5_template: '#22c55e',
    mql5_rules: '#f59e0b',
    mql5_patterns: '#3b82f6'
};

// Tier badge colors
const tierColors = {
    0: { bg: '#ef444430', text: '#ef4444', label: 'T0' },
    1: { bg: '#f59e0b30', text: '#f59e0b', label: 'T1' },
    2: { bg: '#3b82f630', text: '#3b82f6', label: 'T2' },
    3: { bg: '#6b728030', text: '#9ca3af', label: 'T3' }
};

// Main tabs configuration
const MAIN_TABS = [
    { value: 'qai_chat', label: 'Q.AI Chat', icon: <ChatIcon />, description: 'General Q.AI conversation prompts' },
    { value: 'mql5_coding', label: 'MQL5 Coding', icon: <CodeIcon />, description: 'EA templates, rules, and patterns' }
];

const AdminPromptManagement = () => {
    // Main tab state
    const [activeTab, setActiveTab] = useState('qai_chat');

    // State
    const [prompts, setPrompts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Modal states
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [testModalOpen, setTestModalOpen] = useState(false);
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [selectedPrompt, setSelectedPrompt] = useState(null);
    const [isCreating, setIsCreating] = useState(false);

    // Import/Export dialog states
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [viewPrompt, setViewPrompt] = useState(null);
    const [importJson, setImportJson] = useState('');
    const [importFile, setImportFile] = useState(null);
    const [importError, setImportError] = useState(null);
    const [parsedPrompts, setParsedPrompts] = useState([]);
    const [isExporting, setIsExporting] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef(null);

    // Pagination state - enhanced for rows per page options
    const [rowsPerPageOptions] = useState([5, 10, 25, 50]);

    // ─────────────────────────────────────────────────────────────────────────
    // Data Fetching
    // ─────────────────────────────────────────────────────────────────────────

    const fetchPrompts = useCallback(async () => {
        setLoading(true);
        setError(null);
        const result = await getPrompts();
        if (result.success) {
            setPrompts(result.data);
        } else {
            setError(result.error);
            toast.error(result.error);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchPrompts();
    }, [fetchPrompts]);

    // ─────────────────────────────────────────────────────────────────────────
    // Filtering & Pagination
    // ─────────────────────────────────────────────────────────────────────────

    // Filter out MQL5 prompts from Q.AI Chat tab (they have their own tab)
    const qaiChatPrompts = prompts.filter(p => !MQL5_CATEGORIES.includes(p.category));

    const filteredPrompts = qaiChatPrompts.filter(prompt => {
        const matchesSearch = searchQuery === '' ||
            prompt.promptName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            prompt.promptKey?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            prompt.description?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = categoryFilter === 'all' || prompt.category === categoryFilter;
        const matchesStatus = statusFilter === 'all' ||
            (statusFilter === 'active' && prompt.isActive) ||
            (statusFilter === 'inactive' && !prompt.isActive);
        return matchesSearch && matchesCategory && matchesStatus;
    });

    const paginatedPrompts = filteredPrompts.slice(
        (page - 1) * rowsPerPage,
        page * rowsPerPage
    );

    const pageCount = Math.ceil(filteredPrompts.length / rowsPerPage);

    // ─────────────────────────────────────────────────────────────────────────
    // Stats Calculation
    // ─────────────────────────────────────────────────────────────────────────

    const stats = {
        total: qaiChatPrompts.length,
        active: qaiChatPrompts.filter(p => p.isActive).length,
        system: qaiChatPrompts.filter(p => p.category === 'system').length,
        rules: qaiChatPrompts.filter(p => p.category === 'rules').length
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Action Handlers
    // ─────────────────────────────────────────────────────────────────────────

    const handleMenuClose = () => {
        // Menu close handler (kept for consistency with existing handlers)
    };

    const handleEdit = (prompt) => {
        setSelectedPrompt(prompt);
        setIsCreating(false);
        setEditModalOpen(true);
        handleMenuClose();
    };

    const handleCreate = () => {
        setSelectedPrompt(null);
        setIsCreating(true);
        setEditModalOpen(true);
    };

    const handleTest = (prompt) => {
        setSelectedPrompt(prompt);
        setTestModalOpen(true);
        handleMenuClose();
    };

    const handleHistory = (prompt) => {
        setSelectedPrompt(prompt);
        setHistoryModalOpen(true);
        handleMenuClose();
    };

    const handleDelete = async (prompt) => {
        if (prompt.isRequired) {
            toast.error('Cannot delete required prompts');
            handleMenuClose();
            return;
        }

        if (!window.confirm(`Are you sure you want to delete "${prompt.promptName}"?`)) {
            handleMenuClose();
            return;
        }

        const result = await deletePrompt(prompt.promptKey);
        if (result.success) {
            toast.success('Prompt deleted successfully');
            fetchPrompts();
        } else {
            toast.error(result.error);
        }
        handleMenuClose();
    };

    const handleModalClose = () => {
        setEditModalOpen(false);
        setTestModalOpen(false);
        setHistoryModalOpen(false);
        setSelectedPrompt(null);
    };

    const handleSaveSuccess = () => {
        handleModalClose();
        fetchPrompts();
        toast.success(isCreating ? 'Prompt created successfully' : 'Prompt updated successfully');
    };

    // ─────────────────────────────────────────────────────────────────────────
    // View Prompt Handler
    // ─────────────────────────────────────────────────────────────────────────

    const handleView = (prompt) => {
        setViewPrompt(prompt);
        setViewDialogOpen(true);
        handleMenuClose();
    };

    const handleCopyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success('Copied to clipboard');
        } catch (err) {
            toast.error('Failed to copy to clipboard');
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Export Handlers
    // ─────────────────────────────────────────────────────────────────────────

    const handleExport = async () => {
        setIsExporting(true);
        const result = await exportPrompts();
        if (result.success) {
            // Download as JSON file
            const dataStr = JSON.stringify(result.data, null, 2);
            const blob = new Blob([dataStr], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            const date = new Date().toISOString().split('T')[0];
            link.href = url;
            link.download = `prompts_export_${date}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            toast.success(`Exported ${result.data.length} prompts`);
            setExportDialogOpen(false);
        } else {
            toast.error(result.error);
        }
        setIsExporting(false);
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Import Handlers
    // ─────────────────────────────────────────────────────────────────────────

    const resetImportState = () => {
        setImportJson('');
        setImportFile(null);
        setImportError(null);
        setParsedPrompts([]);
    };

    const parseImportData = (jsonString) => {
        try {
            const data = JSON.parse(jsonString);
            // Support both formats: direct array or wrapped object
            let prompts;
            if (Array.isArray(data)) {
                prompts = data;
            } else if (data.prompts && Array.isArray(data.prompts)) {
                prompts = data.prompts;
            } else {
                throw new Error('Invalid format. Expected array or object with "prompts" array.');
            }

            // Validate each prompt has required fields
            const validPrompts = prompts.filter(p =>
                p.promptKey && p.promptName && p.promptText
            );

            if (validPrompts.length === 0) {
                throw new Error('No valid prompts found. Each prompt must have promptKey, promptName, and promptText.');
            }

            setImportError(null);
            setParsedPrompts(validPrompts);
            return validPrompts;
        } catch (err) {
            setImportError(err.message);
            setParsedPrompts([]);
            return [];
        }
    };

    const handleJsonPaste = (e) => {
        const value = e.target.value;
        setImportJson(value);
        if (value.trim()) {
            parseImportData(value);
        } else {
            setImportError(null);
            setParsedPrompts([]);
        }
    };

    const handleFileDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const file = e.dataTransfer?.files?.[0] || e.target?.files?.[0];
        if (file && file.type === 'application/json') {
            setImportFile(file);
            const reader = new FileReader();
            reader.onload = (event) => {
                const content = event.target.result;
                setImportJson(content);
                parseImportData(content);
            };
            reader.readAsText(file);
        } else if (file) {
            setImportError('Please upload a JSON file');
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleImport = async () => {
        if (parsedPrompts.length === 0) {
            toast.error('No valid prompts to import');
            return;
        }

        setIsImporting(true);
        const result = await bulkImportPrompts(parsedPrompts);
        if (result.success) {
            toast.success(`Successfully imported ${parsedPrompts.length} prompts`);
            setImportDialogOpen(false);
            resetImportState();
            fetchPrompts();
        } else {
            toast.error(result.error);
        }
        setIsImporting(false);
    };

    const handleRowsPerPageChange = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(1);
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Render Stats Card
    // ─────────────────────────────────────────────────────────────────────────

    const StatCard = ({ title, value, icon, color, onClick, isActive }) => (
        <Paper
            onClick={onClick}
            sx={{
                p: 2.5,
                background: isActive ? `${color}15` : theme.colors.bg.secondary,
                border: `1px solid ${isActive ? color : theme.colors.border.primary}`,
                borderRadius: theme.borderRadius.md,
                flex: 1,
                minWidth: 140,
                cursor: onClick ? 'pointer' : 'default',
                transition: theme.transitions.hover,
                '&:hover': onClick ? {
                    background: `${color}10`,
                    borderColor: color,
                    transform: 'translateY(-2px)'
                } : {}
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: theme.borderRadius.sm,
                        background: `${color}20`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    {icon}
                </Box>
                <Box>
                    <Typography
                        sx={{
                            color: theme.colors.text.quaternary,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                        }}
                    >
                        {title}
                    </Typography>
                    <Typography
                        sx={{
                            color: theme.colors.text.primary,
                            fontSize: '1.5rem',
                            fontWeight: 700,
                            lineHeight: 1.2
                        }}
                    >
                        {loading ? <Skeleton width={40} /> : value}
                    </Typography>
                </Box>
            </Box>
        </Paper>
    );

    // ─────────────────────────────────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────────────────────────────────

    return (
        <Box sx={{ p: 3, background: theme.colors.bg.primary, minHeight: '100vh' }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography
                    variant="h4"
                    sx={{
                        color: theme.colors.text.primary,
                        fontWeight: 700,
                        mb: 0.5
                    }}
                >
                    Prompt Management
                </Typography>
                <Typography sx={{ color: theme.colors.text.quaternary }}>
                    Manage Q.AI system prompts, MQL5 templates, and response rules
                </Typography>
            </Box>

            {/* Main Tabs */}
            <Paper
                sx={{
                    background: theme.colors.bg.secondary,
                    border: `1px solid ${theme.colors.border.primary}`,
                    borderRadius: theme.borderRadius.md,
                    mb: 3,
                    overflow: 'hidden'
                }}
            >
                <Tabs
                    value={activeTab}
                    onChange={(_, value) => setActiveTab(value)}
                    sx={{
                        borderBottom: `1px solid ${theme.colors.border.primary}`,
                        '& .MuiTabs-indicator': { backgroundColor: theme.colors.accent.primary, height: 3 }
                    }}
                >
                    {MAIN_TABS.map(tab => (
                        <Tab
                            key={tab.value}
                            value={tab.value}
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ color: activeTab === tab.value ? theme.colors.accent.primary : theme.colors.text.quaternary }}>
                                        {tab.icon}
                                    </Box>
                                    <Typography sx={{
                                        color: activeTab === tab.value ? theme.colors.text.primary : theme.colors.text.tertiary,
                                        fontWeight: activeTab === tab.value ? 600 : 400
                                    }}>
                                        {tab.label}
                                    </Typography>
                                </Box>
                            }
                            sx={{ textTransform: 'none', py: 2, px: 3 }}
                        />
                    ))}
                </Tabs>
            </Paper>

            {/* MQL5 Coding Tab */}
            {activeTab === 'mql5_coding' && <MQL5PromptsTab />}

            {/* Q.AI Chat Tab (Original Content) */}
            {activeTab === 'qai_chat' && (
                <>
                    {/* Stats Cards */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <StatCard
                    title="Total Prompts"
                    value={stats.total}
                    icon={<DescriptionIcon sx={{ color: theme.colors.accent.primary }} />}
                    color={theme.colors.accent.primary}
                    onClick={() => { setCategoryFilter('all'); setStatusFilter('all'); setPage(1); }}
                    isActive={categoryFilter === 'all' && statusFilter === 'all'}
                />
                <StatCard
                    title="Active"
                    value={stats.active}
                    icon={<CheckCircleIcon sx={{ color: theme.colors.status.success }} />}
                    color={theme.colors.status.success}
                    onClick={() => { setStatusFilter('active'); setCategoryFilter('all'); setPage(1); }}
                    isActive={statusFilter === 'active'}
                />
                <StatCard
                    title="System"
                    value={stats.system}
                    icon={<LockIcon sx={{ color: categoryColors.system }} />}
                    color={categoryColors.system}
                    onClick={() => { setCategoryFilter('system'); setStatusFilter('all'); setPage(1); }}
                    isActive={categoryFilter === 'system'}
                />
                <StatCard
                    title="Rules"
                    value={stats.rules}
                    icon={<DescriptionIcon sx={{ color: categoryColors.rules }} />}
                    color={categoryColors.rules}
                    onClick={() => { setCategoryFilter('rules'); setStatusFilter('all'); setPage(1); }}
                    isActive={categoryFilter === 'rules'}
                />
            </Box>

            {/* Search & Filter Bar */}
            <Paper
                sx={{
                    p: 2,
                    mb: 3,
                    background: theme.colors.bg.secondary,
                    border: `1px solid ${theme.colors.border.primary}`,
                    borderRadius: theme.borderRadius.md,
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center',
                    flexWrap: 'wrap'
                }}
            >
                {/* Search */}
                <TextField
                    placeholder="Search prompts..."
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                    size="small"
                    sx={{
                        flex: 1,
                        minWidth: 200,
                        '& .MuiOutlinedInput-root': {
                            background: theme.colors.bg.tertiary,
                            color: theme.colors.text.primary,
                            '& fieldset': { borderColor: theme.colors.border.primary },
                            '&:hover fieldset': { borderColor: theme.colors.border.tertiary },
                            '&.Mui-focused fieldset': { borderColor: theme.colors.accent.primary }
                        },
                        '& .MuiInputBase-input::placeholder': { color: theme.colors.text.placeholder }
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: theme.colors.text.quaternary }} />
                            </InputAdornment>
                        )
                    }}
                />

                {/* Category Filter */}
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <Select
                        value={categoryFilter}
                        onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
                        sx={{
                            background: theme.colors.bg.tertiary,
                            color: theme.colors.text.primary,
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.colors.border.primary },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.colors.border.tertiary },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.colors.accent.primary },
                            '& .MuiSvgIcon-root': { color: theme.colors.text.quaternary }
                        }}
                    >
                        <MenuItem value="all">All Categories</MenuItem>
                        {PROMPT_CATEGORIES.map(cat => (
                            <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Status Filter */}
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                        value={statusFilter}
                        onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                        sx={{
                            background: theme.colors.bg.tertiary,
                            color: theme.colors.text.primary,
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.colors.border.primary },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.colors.border.tertiary },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.colors.accent.primary },
                            '& .MuiSvgIcon-root': { color: theme.colors.text.quaternary }
                        }}
                    >
                        <MenuItem value="all">All Status</MenuItem>
                        <MenuItem value="active">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CheckCircleIcon sx={{ fontSize: 16, color: theme.colors.status.success }} />
                                Active
                            </Box>
                        </MenuItem>
                        <MenuItem value="inactive">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CancelIcon sx={{ fontSize: 16, color: theme.colors.status.error }} />
                                Inactive
                            </Box>
                        </MenuItem>
                    </Select>
                </FormControl>

                {/* Actions */}
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FileDownloadIcon />}
                    onClick={() => setExportDialogOpen(true)}
                    sx={{
                        color: theme.colors.text.tertiary,
                        borderColor: theme.colors.border.primary,
                        '&:hover': {
                            borderColor: theme.colors.accent.primary,
                            color: theme.colors.accent.tertiary
                        }
                    }}
                >
                    Export
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FileUploadIcon />}
                    onClick={() => setImportDialogOpen(true)}
                    sx={{
                        color: theme.colors.text.tertiary,
                        borderColor: theme.colors.border.primary,
                        '&:hover': {
                            borderColor: theme.colors.accent.primary,
                            color: theme.colors.accent.tertiary
                        }
                    }}
                >
                    Import
                </Button>
                <Tooltip title="Refresh">
                    <IconButton
                        onClick={fetchPrompts}
                        disabled={loading}
                        sx={{ color: theme.colors.text.tertiary }}
                    >
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreate}
                    sx={{
                        background: theme.colors.accent.primary,
                        '&:hover': { background: theme.colors.accent.secondary }
                    }}
                >
                    Add New
                </Button>
            </Paper>

            {/* Error Alert */}
            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 3, background: `${theme.colors.status.error}20`, color: theme.colors.status.error }}
                    onClose={() => setError(null)}
                >
                    {error}
                </Alert>
            )}

            {/* Prompts Table */}
            <TableContainer
                component={Paper}
                sx={{
                    background: theme.colors.bg.secondary,
                    border: `1px solid ${theme.colors.border.primary}`,
                    borderRadius: theme.borderRadius.md,
                    mb: 2
                }}
            >
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ color: theme.colors.text.quaternary, fontWeight: 600, borderColor: theme.colors.border.primary }}>
                                Name
                            </TableCell>
                            <TableCell sx={{ color: theme.colors.text.quaternary, fontWeight: 600, borderColor: theme.colors.border.primary }}>
                                Key
                            </TableCell>
                            <TableCell sx={{ color: theme.colors.text.quaternary, fontWeight: 600, borderColor: theme.colors.border.primary }}>
                                Category
                            </TableCell>
                            <TableCell sx={{ color: theme.colors.text.quaternary, fontWeight: 600, borderColor: theme.colors.border.primary }}>
                                Status
                            </TableCell>
                            <TableCell sx={{ color: theme.colors.text.quaternary, fontWeight: 600, borderColor: theme.colors.border.primary }} align="right">
                                Actions
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            // Loading skeleton
                            [...Array(5)].map((_, i) => (
                                <TableRow key={i}>
                                    <TableCell sx={{ borderColor: theme.colors.border.primary }}>
                                        <Skeleton variant="text" width={150} sx={{ bgcolor: theme.colors.bg.tertiary }} />
                                    </TableCell>
                                    <TableCell sx={{ borderColor: theme.colors.border.primary }}>
                                        <Skeleton variant="text" width={100} sx={{ bgcolor: theme.colors.bg.tertiary }} />
                                    </TableCell>
                                    <TableCell sx={{ borderColor: theme.colors.border.primary }}>
                                        <Skeleton variant="rounded" width={70} height={24} sx={{ bgcolor: theme.colors.bg.tertiary }} />
                                    </TableCell>
                                    <TableCell sx={{ borderColor: theme.colors.border.primary }}>
                                        <Skeleton variant="rounded" width={50} height={24} sx={{ bgcolor: theme.colors.bg.tertiary }} />
                                    </TableCell>
                                    <TableCell sx={{ borderColor: theme.colors.border.primary }} align="right">
                                        <Skeleton variant="circular" width={32} height={32} sx={{ bgcolor: theme.colors.bg.tertiary, ml: 'auto' }} />
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : paginatedPrompts.length === 0 ? (
                            // Empty state
                            <TableRow>
                                <TableCell colSpan={5} sx={{ borderColor: theme.colors.border.primary, textAlign: 'center', py: 6 }}>
                                    <DescriptionIcon sx={{ fontSize: 48, color: theme.colors.text.placeholder, mb: 1 }} />
                                    <Typography sx={{ color: theme.colors.text.quaternary }}>
                                        {searchQuery || categoryFilter !== 'all' || statusFilter !== 'all' ? 'No prompts match your filters' : 'No prompts found'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            // Data rows
                            paginatedPrompts.map((prompt) => (
                                <TableRow
                                    key={prompt.promptKey}
                                    sx={{
                                        transition: theme.transitions.hover,
                                        '&:hover': { background: theme.colors.bg.tertiary }
                                    }}
                                >
                                    <TableCell sx={{ borderColor: theme.colors.border.primary }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {prompt.isRequired && (
                                                <Tooltip title="Required prompt (cannot delete)">
                                                    <LockIcon sx={{ fontSize: 16, color: theme.colors.text.quaternary }} />
                                                </Tooltip>
                                            )}
                                            {/* Tier badge */}
                                            {prompt.metadata?.tier !== undefined && (
                                                <Tooltip title={METADATA_TIERS.find(t => t.value === prompt.metadata.tier)?.description || `Tier ${prompt.metadata.tier}`}>
                                                    <Chip
                                                        label={tierColors[prompt.metadata.tier]?.label || `T${prompt.metadata.tier}`}
                                                        size="small"
                                                        sx={{
                                                            height: 18,
                                                            fontSize: '0.65rem',
                                                            fontWeight: 600,
                                                            background: tierColors[prompt.metadata.tier]?.bg || '#6b728030',
                                                            color: tierColors[prompt.metadata.tier]?.text || '#9ca3af',
                                                            '& .MuiChip-label': { px: 0.75 }
                                                        }}
                                                    />
                                                </Tooltip>
                                            )}
                                            <Typography sx={{ color: theme.colors.text.primary, fontWeight: 500 }}>
                                                {prompt.promptName}
                                            </Typography>
                                        </Box>
                                        {prompt.description && (
                                            <Typography
                                                sx={{
                                                    color: theme.colors.text.quaternary,
                                                    fontSize: '0.75rem',
                                                    mt: 0.25,
                                                    maxWidth: 300,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {prompt.description}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ borderColor: theme.colors.border.primary }}>
                                        <Typography
                                            sx={{
                                                color: theme.colors.text.tertiary,
                                                fontFamily: 'monospace',
                                                fontSize: '0.85rem'
                                            }}
                                        >
                                            {prompt.promptKey}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ borderColor: theme.colors.border.primary }}>
                                        <Chip
                                            label={prompt.category}
                                            size="small"
                                            sx={{
                                                background: `${categoryColors[prompt.category] || theme.colors.accent.primary}20`,
                                                color: categoryColors[prompt.category] || theme.colors.accent.primary,
                                                fontWeight: 500,
                                                textTransform: 'capitalize'
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ borderColor: theme.colors.border.primary }}>
                                        <Chip
                                            label={prompt.isActive ? 'ON' : 'OFF'}
                                            size="small"
                                            icon={prompt.isActive ?
                                                <CheckCircleIcon sx={{ fontSize: 14 }} /> :
                                                <CancelIcon sx={{ fontSize: 14 }} />
                                            }
                                            sx={{
                                                background: prompt.isActive ? `${theme.colors.status.success}20` : `${theme.colors.status.error}20`,
                                                color: prompt.isActive ? theme.colors.status.success : theme.colors.status.error,
                                                fontWeight: 600,
                                                '& .MuiChip-icon': {
                                                    color: 'inherit'
                                                }
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ borderColor: theme.colors.border.primary }} align="right">
                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                            <Tooltip title="View">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleView(prompt)}
                                                    sx={{ color: theme.colors.text.tertiary, '&:hover': { color: theme.colors.status.success } }}
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Edit">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEdit(prompt)}
                                                    sx={{ color: theme.colors.text.tertiary, '&:hover': { color: theme.colors.accent.tertiary } }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Test">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleTest(prompt)}
                                                    sx={{ color: theme.colors.text.tertiary, '&:hover': { color: theme.colors.status.info } }}
                                                >
                                                    <ScienceIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="History">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleHistory(prompt)}
                                                    sx={{ color: theme.colors.text.tertiary, '&:hover': { color: theme.colors.status.warning } }}
                                                >
                                                    <HistoryIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {!prompt.isRequired && (
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDelete(prompt)}
                                                        sx={{ color: theme.colors.text.tertiary, '&:hover': { color: theme.colors.status.error } }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination & Info */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.875rem' }}>
                        Showing {paginatedPrompts.length} of {filteredPrompts.length} prompts
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 80 }}>
                        <Select
                            value={rowsPerPage}
                            onChange={handleRowsPerPageChange}
                            sx={{
                                background: theme.colors.bg.tertiary,
                                color: theme.colors.text.primary,
                                fontSize: '0.875rem',
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.colors.border.primary },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.colors.border.tertiary },
                                '& .MuiSvgIcon-root': { color: theme.colors.text.quaternary }
                            }}
                        >
                            {rowsPerPageOptions.map(option => (
                                <MenuItem key={option} value={option}>{option} / page</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                {pageCount > 1 && (
                    <Pagination
                        count={pageCount}
                        page={page}
                        onChange={(_, newPage) => setPage(newPage)}
                        sx={{
                            '& .MuiPaginationItem-root': {
                                color: theme.colors.text.tertiary,
                                '&.Mui-selected': {
                                    background: theme.colors.accent.primary,
                                    color: theme.colors.text.primary
                                }
                            }
                        }}
                    />
                )}
            </Box>

            {/* Modals */}
            <EditPromptModal
                open={editModalOpen}
                prompt={selectedPrompt}
                isCreating={isCreating}
                onClose={handleModalClose}
                onSuccess={handleSaveSuccess}
            />
            <TestPromptModal
                open={testModalOpen}
                prompt={selectedPrompt}
                onClose={handleModalClose}
            />
            <VersionHistoryModal
                open={historyModalOpen}
                prompt={selectedPrompt}
                onClose={handleModalClose}
                onRestore={() => {
                    handleModalClose();
                    fetchPrompts();
                }}
            />

            {/* Export Dialog */}
            <Dialog
                open={exportDialogOpen}
                onClose={() => setExportDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        background: theme.colors.bg.secondary,
                        border: `1px solid ${theme.colors.border.primary}`,
                        borderRadius: theme.borderRadius.md
                    }
                }}
            >
                <DialogTitle sx={{ color: theme.colors.text.primary, borderBottom: `1px solid ${theme.colors.border.primary}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FileDownloadIcon sx={{ color: theme.colors.accent.primary }} />
                        Export Prompts
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Typography sx={{ color: theme.colors.text.secondary, mb: 2 }}>
                        Export all prompts as a JSON file. This file can be used to backup your prompts or import them into another environment.
                    </Typography>
                    <Box sx={{
                        p: 2,
                        background: theme.colors.bg.tertiary,
                        borderRadius: theme.borderRadius.sm,
                        border: `1px solid ${theme.colors.border.primary}`
                    }}>
                        <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.875rem' }}>
                            File name: <strong style={{ color: theme.colors.text.primary }}>prompts_export_{new Date().toISOString().split('T')[0]}.json</strong>
                        </Typography>
                        <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.875rem', mt: 1 }}>
                            Total prompts: <strong style={{ color: theme.colors.text.primary }}>{prompts.length}</strong>
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: `1px solid ${theme.colors.border.primary}` }}>
                    <Button
                        onClick={() => setExportDialogOpen(false)}
                        sx={{ color: theme.colors.text.tertiary }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleExport}
                        disabled={isExporting}
                        startIcon={isExporting ? <CircularProgress size={16} /> : <FileDownloadIcon />}
                        sx={{
                            background: theme.colors.accent.primary,
                            '&:hover': { background: theme.colors.accent.secondary }
                        }}
                    >
                        {isExporting ? 'Exporting...' : 'Download JSON'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Import Dialog */}
            <Dialog
                open={importDialogOpen}
                onClose={() => { setImportDialogOpen(false); resetImportState(); }}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        background: theme.colors.bg.secondary,
                        border: `1px solid ${theme.colors.border.primary}`,
                        borderRadius: theme.borderRadius.md
                    }
                }}
            >
                <DialogTitle sx={{ color: theme.colors.text.primary, borderBottom: `1px solid ${theme.colors.border.primary}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FileUploadIcon sx={{ color: theme.colors.accent.primary }} />
                        Import Prompts
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Typography sx={{ color: theme.colors.text.secondary, mb: 3 }}>
                        Import prompts from a JSON file. You can either drag & drop a file or paste JSON directly.
                    </Typography>

                    {/* Drag & Drop Zone */}
                    <Box
                        onDrop={handleFileDrop}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onClick={() => fileInputRef.current?.click()}
                        sx={{
                            p: 4,
                            border: `2px dashed ${isDragOver ? theme.colors.accent.primary : theme.colors.border.tertiary}`,
                            borderRadius: theme.borderRadius.md,
                            background: isDragOver ? `${theme.colors.accent.primary}10` : theme.colors.bg.tertiary,
                            textAlign: 'center',
                            cursor: 'pointer',
                            transition: theme.transitions.hover,
                            mb: 3,
                            '&:hover': {
                                borderColor: theme.colors.accent.primary,
                                background: `${theme.colors.accent.primary}10`
                            }
                        }}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".json"
                            onChange={handleFileDrop}
                            style={{ display: 'none' }}
                        />
                        <CloudUploadIcon sx={{ fontSize: 48, color: theme.colors.text.quaternary, mb: 1 }} />
                        <Typography sx={{ color: theme.colors.text.secondary, fontWeight: 500 }}>
                            Drag & drop a JSON file here
                        </Typography>
                        <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.875rem' }}>
                            or click to select a file
                        </Typography>
                        {importFile && (
                            <Chip
                                label={importFile.name}
                                onDelete={() => { setImportFile(null); resetImportState(); }}
                                sx={{
                                    mt: 2,
                                    background: theme.colors.accent.primary,
                                    color: theme.colors.text.primary
                                }}
                            />
                        )}
                    </Box>

                    {/* Or Divider */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                        <Box sx={{ flex: 1, height: 1, background: theme.colors.border.primary }} />
                        <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.875rem' }}>OR</Typography>
                        <Box sx={{ flex: 1, height: 1, background: theme.colors.border.primary }} />
                    </Box>

                    {/* JSON Paste Area */}
                    <TextField
                        multiline
                        rows={8}
                        fullWidth
                        placeholder='Paste JSON here...\n\n// Format 1: Direct array\n[\n  { "promptKey": "...", "promptName": "...", "promptText": "..." }\n]\n\n// Format 2: Wrapped object\n{\n  "prompts": [ ... ]\n}'
                        value={importJson}
                        onChange={handleJsonPaste}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                background: theme.colors.bg.tertiary,
                                color: theme.colors.text.primary,
                                fontFamily: 'monospace',
                                fontSize: '0.85rem',
                                '& fieldset': { borderColor: theme.colors.border.primary },
                                '&:hover fieldset': { borderColor: theme.colors.border.tertiary },
                                '&.Mui-focused fieldset': { borderColor: theme.colors.accent.primary }
                            },
                            '& .MuiInputBase-input::placeholder': { color: theme.colors.text.placeholder }
                        }}
                    />

                    {/* Validation Result */}
                    {importError && (
                        <Alert
                            severity="error"
                            sx={{
                                mt: 2,
                                background: `${theme.colors.status.error}20`,
                                color: theme.colors.status.error,
                                '& .MuiAlert-icon': { color: theme.colors.status.error }
                            }}
                        >
                            {importError}
                        </Alert>
                    )}

                    {parsedPrompts.length > 0 && (
                        <Alert
                            severity="success"
                            sx={{
                                mt: 2,
                                background: `${theme.colors.status.success}20`,
                                color: theme.colors.status.success,
                                '& .MuiAlert-icon': { color: theme.colors.status.success }
                            }}
                        >
                            Found {parsedPrompts.length} valid prompt(s) ready to import
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: `1px solid ${theme.colors.border.primary}` }}>
                    <Button
                        onClick={() => { setImportDialogOpen(false); resetImportState(); }}
                        sx={{ color: theme.colors.text.tertiary }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleImport}
                        disabled={isImporting || parsedPrompts.length === 0}
                        startIcon={isImporting ? <CircularProgress size={16} /> : <FileUploadIcon />}
                        sx={{
                            background: theme.colors.accent.primary,
                            '&:hover': { background: theme.colors.accent.secondary },
                            '&:disabled': { background: theme.colors.bg.quaternary }
                        }}
                    >
                        {isImporting ? 'Importing...' : `Import ${parsedPrompts.length} Prompt(s)`}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Prompt Dialog */}
            <Dialog
                open={viewDialogOpen}
                onClose={() => { setViewDialogOpen(false); setViewPrompt(null); }}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        background: theme.colors.bg.secondary,
                        border: `1px solid ${theme.colors.border.primary}`,
                        borderRadius: theme.borderRadius.md
                    }
                }}
            >
                <DialogTitle sx={{
                    color: theme.colors.text.primary,
                    borderBottom: `1px solid ${theme.colors.border.primary}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <VisibilityIcon sx={{ color: theme.colors.accent.primary }} />
                        {viewPrompt?.promptName || 'View Prompt'}
                    </Box>
                    <IconButton
                        onClick={() => { setViewDialogOpen(false); setViewPrompt(null); }}
                        sx={{ color: theme.colors.text.tertiary }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    {viewPrompt && (
                        <Box>
                            {/* Prompt Details */}
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                                <Box>
                                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', mb: 0.5 }}>
                                        Key
                                    </Typography>
                                    <Typography sx={{ color: theme.colors.text.primary, fontFamily: 'monospace' }}>
                                        {viewPrompt.promptKey}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', mb: 0.5 }}>
                                        Category
                                    </Typography>
                                    <Chip
                                        label={viewPrompt.category}
                                        size="small"
                                        sx={{
                                            background: `${categoryColors[viewPrompt.category] || theme.colors.accent.primary}20`,
                                            color: categoryColors[viewPrompt.category] || theme.colors.accent.primary,
                                            fontWeight: 500,
                                            textTransform: 'capitalize'
                                        }}
                                    />
                                </Box>
                                <Box>
                                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', mb: 0.5 }}>
                                        Status
                                    </Typography>
                                    <Chip
                                        label={viewPrompt.isActive ? 'Active' : 'Inactive'}
                                        size="small"
                                        sx={{
                                            background: viewPrompt.isActive ? `${theme.colors.status.success}20` : `${theme.colors.status.error}20`,
                                            color: viewPrompt.isActive ? theme.colors.status.success : theme.colors.status.error
                                        }}
                                    />
                                </Box>
                                <Box>
                                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', mb: 0.5 }}>
                                        Version
                                    </Typography>
                                    <Typography sx={{ color: theme.colors.text.primary }}>
                                        v{viewPrompt.version || 1}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Description */}
                            {viewPrompt.description && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', mb: 0.5 }}>
                                        Description
                                    </Typography>
                                    <Typography sx={{ color: theme.colors.text.secondary }}>
                                        {viewPrompt.description}
                                    </Typography>
                                </Box>
                            )}

                            {/* Prompt Text */}
                            <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase' }}>
                                        Prompt Text
                                    </Typography>
                                    <Tooltip title="Copy to clipboard">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleCopyToClipboard(viewPrompt.promptText)}
                                            sx={{ color: theme.colors.text.tertiary }}
                                        >
                                            <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Box sx={{
                                    p: 2,
                                    background: theme.colors.bg.tertiary,
                                    borderRadius: theme.borderRadius.sm,
                                    border: `1px solid ${theme.colors.border.primary}`,
                                    maxHeight: 400,
                                    overflow: 'auto'
                                }}>
                                    <Typography
                                        sx={{
                                            color: theme.colors.text.primary,
                                            fontFamily: 'monospace',
                                            fontSize: '0.85rem',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word'
                                        }}
                                    >
                                        {viewPrompt.promptText}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Metadata */}
                            <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${theme.colors.border.primary}` }}>
                                <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem' }}>
                                    Last updated: {viewPrompt.updatedAt ? new Date(viewPrompt.updatedAt).toLocaleString() : 'N/A'}
                                    {viewPrompt.updatedBy && ` by ${viewPrompt.updatedBy}`}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
                </>
            )}
        </Box>
    );
};

export default AdminPromptManagement;
